<template>
  <div class="page-container">
    <div class="main">
      <page ref="page" :url="url" :form="form">
        <template slot="tableSearch">
          <el-form-item :label="$t('text.enabled')">
            <el-select v-model="form.enabled">
              <el-option :label="$t('option.all')" value=""/>
              <el-option :label="$t('text.yes')" value="true"/>
              <el-option :label="$t('text.no')" value="false"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.status')">
            <el-select v-model="form.status">
              <el-option :label="$t('text.normal')" value=""/>
              <el-option :label="$t('text.fullBalance')" value="fullBalance"/>
              <el-option :label="$t('text.trash')" value="trash"/>
              <el-option :label="$t('text.backup')" value="backup"/>
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('text.groups')">
            <el-select v-model="form.group">
              <el-option :label="$t('option.all')" value=""/>
              <el-option v-for="group in groupList" :key="group.value" :label="group.name" :value="group.value"/>
            </el-select>
          </el-form-item>
        </template>

        <template slot="buttonArea">
          <el-button type="primary" size="small" @click="handleAddCardClick">
            {{ $t('text.addCard') }}
          </el-button>
          <el-button type="primary" size="small" plain @click="handleManageGroupClick">
            {{ $t('text.manageCardGroup') }}
          </el-button>
        </template>

        <template slot="pageHeader">
          <div v-loading="statisticsLoading">
            <el-divider/>
            <el-row>
              <el-badge :value="groupStatistics.fullBalance" class="item">
                <el-button type="danger" size="small" plain @click="handleQuerySearchClick('', 'fullBalance', '')">{{ $t('text.fullBalance') }}</el-button>
              </el-badge>
              <el-badge :value="groupStatistics.trash" class="item">
                <el-button type="info" size="small" plain @click="handleQuerySearchClick('', 'trash', '')">{{ $t('text.trash') }}</el-button>
              </el-badge>
              <el-badge :value="groupStatistics.backup" class="item">
                <el-button type="success" size="small" plain @click="handleQuerySearchClick('', 'backup', '')">{{ $t('text.backup') }}</el-button>
              </el-badge>
            </el-row>
            <el-divider
                v-if="groupStatistics.groups !== undefined"
            />
            <el-row
                v-if="groupStatistics.groups !== undefined"
                class="card-groups"
            >
              <el-badge
                  :value="`${group.enabledCardCount}/${group.allCardCount}`"
                  class="info"

                  v-for="group in groupStatistics.groups"
              >
                <el-button size="small" plain @click="handleQuerySearchClick('', '', group.id.toString())">
                  {{ group.name }}
                </el-button>
              </el-badge>
            </el-row>
          </div>
        </template>

        <template slot="tableColumn">
          <el-table-column :label="$t('text.cardId')" align="center" width="100px">
            <template slot-scope="{ row }">
              <div :class="{'card-disabled': !row.available}">{{ row.id }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="name" :label="$t('text.cardName')" align="center" width="250px"/>
          <el-table-column prop="bankName" :label="$t('text.bankName')" align="center" width="250px"/>
          <el-table-column prop="accountNumber" :label="$t('text.accountNumber')" align="center" width="200px"/>
          <el-table-column prop="iban" :label="$t('text.iban')" align="center" width="250px"/>
          <el-table-column :label="$t('text.groups')" align="center" width="200px">
            <template slot-scope="{ row }">
              <el-tag v-if="row.specialGroup === 'trash'" type="info">{{ $t('text.trash') }}</el-tag>
              <el-tag v-else-if="row.specialGroup === 'backup'" type="success">{{ $t('text.backup') }}</el-tag>
              <el-tag v-else v-for="item in row.groupNames">{{ item }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="|" width="25px">|</el-table-column>
          <el-table-column prop="maxBalance" :label="$t('text.maxBalance')" align="center" width="180px"/>
          <el-table-column prop="currentBalance" :label="$t('text.currentBalance')" align="center" width="180px"/>
          <el-table-column prop="enabled" :label="$t('text.enabled')" align="center" :formatter="formatter.formatBoolean"/>
          <el-table-column label="|" width="25px">|</el-table-column>
          <el-table-column prop="displayName" :label="$t('text.displayName')" align="center" width="250px"/>
          <el-table-column prop="displayField" :label="$t('text.displayField')" align="center" width="130px" :formatter="formatter.formatDisplayField"/>
          <el-table-column label="|" width="25px">|</el-table-column>
          <el-table-column prop="creationTime" :label="$t('text.creationTime')" align="center" width="180px" :formatter="formatter.formatDate"/>
          <el-table-column prop="creatorId" :label="$t('text.creator')" align="center" width="150px"/>
          <el-table-column prop="modificationTime" :label="$t('text.updateTime')" align="center" width="180px" :formatter="formatter.formatDate"/>
          <el-table-column prop="modifierId" :label="$t('text.updatedBy')" align="center" width="150px" :formatter="formatter.formatNullableString"/>
          <el-table-column fixed="right" :label="$t('text.actions')" align="center" width="100">
            <template slot-scope="scope">
              <el-button type="primary" size="mini" plain @click="handleEditCardClick(scope.row)">{{
                  $t('text.edit')
                }}
              </el-button>
            </template>
          </el-table-column>
        </template>
      </page>

      <BankCardInfoEdit :show.sync="showCardEditor" :value="rowData" :action="action"/>
      <BankCardGroupEdit :show.sync="showGroupEditor"/>
    </div>
  </div>
</template>

<script>
import Page from "@/components/Page.vue";
import formatter from "@/utils/formatter";
import BankCardInfoEdit from "@/views/route/bankCardInfo/BankCardInfoEdit.vue";
import BankCardGroupEdit from "@/views/route/bankCardInfo/BankCardGroupEdit.vue";

export default {
  name: "BankCardInfo",
  computed: {
    formatter() {
      return formatter;
    }
  },
  components: {BankCardGroupEdit, BankCardInfoEdit, Page},
  data() {
    return {
      url: "/bankCardInfo/selectList",
      form: {
        enabled: "",
        status: "",
        group: ""
      },
      showCardEditor: false,
      showGroupEditor: false,
      rowData: null,
      action: "",
      statisticsLoading: false,
      groupStatistics: {},
      groupList: []
    };
  },
  methods: {
    handleAddCardClick() {
      this.action = "add";
      this.showCardEditor = true;
    },
    handleEditCardClick(row) {
      this.action = "edit";
      this.rowData = row;
      this.showCardEditor = true;
    },
    handleManageGroupClick() {
      this.showGroupEditor = true;
    },
    handleQuerySearchClick(enabled, status, group) {
      this.form.enabled = enabled;
      this.form.status = status;
      this.form.group = group;

      this.$refs.page.onSubmit();
    },
    refreshStatistics() {
      this.statisticsLoading = true;
      this.$axios
          .get("/bankCardInfo/selectBankCardGroupStatistics", {params: {}})
          .then((res) => {
            this.statisticsLoading = false;
            if (res.code === 200) {
              this.groupStatistics = res.data;
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
              return false;
            }
          })
          .catch((res) => {
            this.$notify.error({
              title: this.$t("text.prompt"),
              message: res.message
            });
            this.statisticsLoading = false;
          });
    },
    refreshGroupOptions() {
      this.$axios
          .get("/bankCardInfo/selectBankCardGroupOptions", {params: {}})
          .then((res) => {
            if (res.code === 200) {
              this.groupList = res.data;
              return true;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
              this.closeDialog();
              return false;
            }
          });
    }
  },
  created() {
    this.refreshStatistics();
    this.refreshGroupOptions();
    this.$bus.$on("refresh", () => this.refreshStatistics());
  }
};
</script>

<style scoped>
.el-tag {
  margin-top: 10px;
}

.el-tag + .el-tag {
  margin-left: 10px;
}

.cell .el-tag:last-child {
  margin-bottom: 10px;
}

.el-row > .el-badge {
  margin-right: 40px;
}

.el-row.card-groups > .el-badge {
  margin-top: 10px;
  margin-bottom: 10px;
}

.el-row > .el-badge:last-child {
  margin-right: 0;
}

.form .el-row:first-child {
  margin-bottom: 10px;
}

</style>
<style>
.card-disabled {
  background-color: #ffb3b3;
}
</style>